import { Box } from "@/Components/v2/atoms/Box";
import { Typography } from "@/Components/v2/atoms/Typography";
import { Stack } from "@/Components/v2/atoms/Stack";
import RecommendationCard from "./RecommendationCard";
import { InfoOutlined } from "@mui/icons-material";
import { useTheme } from "@mui/material";
import { FormatCurrency } from "@/hooks/useConfig/currency";
import { useScenariosV3Routes } from "@/pages/scenario-builder/v3/hooks/useScenariosV3Routes";
import { useApproveScenarioForActionPlan } from "@/pages/scenario-builder/v3/api/v2/approveScenarioForActionPlan";
import { ExtendedActionPlanDetail } from "@/pages/scenario-builder/v3/api/v2/getActionPlan";
import AlertTight from "@/Components/v2/atoms/Alert/AlertTight";
import { useUnapproveScenarioForActionPlan } from "@/pages/scenario-builder/v3/api/v2/unapproveScenarioForActionPlan";
import { useState } from "react";
import AboutRecommendationsModal from "./AboutRecommendationsModal";
import { useCallback } from "react";
import { useAnalytics } from "@/hooks/useAnalytics";
import dayjs from "dayjs";

export type RecommendationAction = "approve" | "unapprove" | "view";

type RecommendationsProps = {
  actionPlan: ExtendedActionPlanDetail;
  formatCurrency: FormatCurrency;
};

export const Recommendations = ({ actionPlan, formatCurrency }: RecommendationsProps) => {
  const theme = useTheme();
  const { navigateToScenarioResults } = useScenariosV3Routes();
  const { scenarioRecommendationViewed } = useAnalytics();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const { uuid: actionPlanUuid, recommendations, isActionPlanRefreshed, liveScenario } = actionPlan;

  const approveScenarioForActionPlan = useApproveScenarioForActionPlan();
  const unapproveScenarioForActionPlan = useUnapproveScenarioForActionPlan();

  if (!isActionPlanRefreshed) {
    return (
      <Box
        sx={{
          backgroundColor: theme.palette.infoBanner.background,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: 2,
          borderRadius: 2,
        }}
      >
        <Stack direction="row" spacing={2} alignItems="center" mr={6}>
          <Stack direction="row" spacing={1}>
            <InfoOutlined sx={{ color: theme.palette.infoIcon.color, fontSize: 24 }} />
            <Stack>
              <Typography variant="body2" fontWeight={600} mb={0.5}>
                We haven&apos;t received any updates on this Action Plan yet
              </Typography>
              <Typography variant="body2">
                Check back in once you&apos;ve refreshed your data for an update on how you&apos;re
                tracking towards achieving this goal.
              </Typography>
            </Stack>
          </Stack>
        </Stack>
      </Box>
    );
  }

  const handleViewScenario = (scenarioId: string) => {
    navigateToScenarioResults(scenarioId, actionPlanUuid);
    scenarioRecommendationViewed({
      actionPlanId: actionPlanUuid,
      actionPlanName: actionPlan.name,
      actionPlanLength: dayjs(actionPlan.endDate).diff(dayjs(actionPlan.startDate), "months"),
      actionPlanSpend: actionPlan.spendPlannedTotal,
      actionPlanStatus: actionPlan.status,
      actionPlanRevenueGoal: actionPlan.revenueTarget,
      actionPlanRevenueForecast: actionPlan.revenueForecastToDate,
    });
  };

  const handleApproveScenario = (scenarioId: string) => {
    approveScenarioForActionPlan.mutate({
      actionPlanUuid,
      scenarioId,
    });
  };

  const handleUnapproveScenario = (scenarioId: string) => {
    unapproveScenarioForActionPlan.mutate({
      actionPlanUuid,
      scenarioId,
    });
  };

  const createHandleAction = useCallback(
    (scenarioId: string | undefined) => {
      return (action: RecommendationAction) => {
        if (!scenarioId) return;
        switch (action) {
          case "approve":
            handleApproveScenario(scenarioId);
            break;
          case "unapprove":
            handleUnapproveScenario(scenarioId);
            break;
          case "view":
            handleViewScenario(scenarioId);
            break;
        }
      };
    },
    [handleApproveScenario, handleUnapproveScenario, handleViewScenario]
  );

  const isApprovingScenario = (scenarioId: string) => {
    return (
      approveScenarioForActionPlan.isPending &&
      scenarioId === approveScenarioForActionPlan.variables?.scenarioId
    );
  };

  const isUnapprovingScenario = (scenarioId: string) => {
    return (
      unapproveScenarioForActionPlan.isPending &&
      scenarioId === unapproveScenarioForActionPlan.variables?.scenarioId
    );
  };

  return (
    <Stack direction="column" gap={1}>
      <Stack direction="row" alignItems="center" spacing={3}>
        <Typography variant="h6" fontSize={20} fontWeight={600}>
          Recommendations
        </Typography>
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          onClick={() => setIsModalOpen(true)}
          sx={{
            cursor: "pointer",
            "&:hover": {
              opacity: 0.7,
            },
          }}
        >
          <Typography variant="body2" fontWeight={600}>
            How this works
          </Typography>
          <InfoOutlined sx={{ fontSize: 20 }} />
        </Stack>
      </Stack>
      {recommendations && recommendations.length > 0 ? (
        <Stack direction="row" justifyContent="space-between" spacing={2}>
          {recommendations.map((recommendation, index) => {
            const { scenario } = recommendation;
            if (!scenario || !scenario?.uuid) return null;
            const { uuid: scenarioId } = scenario;

            return (
              <RecommendationCard
                key={`recommendation-card-${scenarioId}-${index}`}
                recommendation={recommendation}
                handleAction={createHandleAction(scenarioId)}
                isApproveScenarioSending={isApprovingScenario(scenarioId)}
                isUnapproveScenarioSending={isUnapprovingScenario(scenarioId)}
                isApprovedScenario={scenarioId === liveScenario?.uuid}
                formatCurrency={formatCurrency}
              />
            );
          })}
        </Stack>
      ) : (
        <AlertTight
          icon={<InfoOutlined sx={{ color: "#FC9429" }} />}
          title="Recommendations could not be generated"
          text="We were unable to generate any recommendations for this Action Plan. This may be due to an error, or because no new scenarios could be generated under the existing constraints. For more information, contact your Marketing Science Partner."
          textColor="black"
          sx={{ backgroundColor: theme.palette.mode === "dark" ? "#FC942966" : "#FCE5C4" }}
        />
      )}
      <AboutRecommendationsModal open={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </Stack>
  );
};

export default Recommendations;
